import { useEffect, useState, type ReactNode } from "react";
import { PostHogProvider } from "posthog-js/react";
import { AnalyticsService } from "@/lib/analytics/analytics.service";

interface LazyPostHogProviderProps {
	children: ReactNode;
	apiKey?: string;
	apiHost?: string;
	enabled?: boolean;
}

/**
 * Lazy-loaded PostHog provider that defers initialization until after initial render
 * to prevent blocking the main thread and app startup.
 *
 * Disabled by default. Only enables if:
 * - enabled prop is explicitly true
 * - Not running on localhost
 * - API key and host are configured
 */
export function LazyPostHogProvider({
	children,
	apiKey,
	apiHost,
	enabled = false,
}: LazyPostHogProviderProps) {
	const [shouldLoad, setShouldLoad] = useState(false);

	useEffect(() => {
		// Skip PostHog initialization if not enabled
		if (!enabled) {
			return;
		}

		// Skip PostHog initialization if API key or host is not configured
		if (!apiKey || !apiHost) {
			return;
		}

		// Skip PostHog on localhost
		const isLocalhost =
			window.location.hostname === "localhost" ||
			window.location.hostname === "127.0.0.1" ||
			window.location.hostname === "" ||
			window.location.hostname.startsWith("192.168.") ||
			window.location.hostname.startsWith("10.");

		if (isLocalhost) {
			return;
		}

		// Defer PostHog loading until after initial render and critical app initialization
		// Use requestIdleCallback if available, otherwise use setTimeout
		if (window.requestIdleCallback) {
			window.requestIdleCallback(
				() => {
					setShouldLoad(true);
				},
				{ timeout: 2000 }, // Fallback after 2 seconds if idle callback never fires
			);
		} else {
			// Fallback for browsers without requestIdleCallback
			setTimeout(() => {
				setShouldLoad(true);
			}, 1000);
		}
	}, [enabled, apiKey, apiHost]);

	// Render children immediately without PostHog if not enabled, not configured, or not yet loaded
	if (!enabled || !apiKey || !apiHost || !shouldLoad) {
		return <>{children}</>;
	}

	const isProduction = import.meta.env.MODE === "production";

	// Only set cookie_domain if we're actually on the podhive.ai domain
	// This prevents "invalid domain" errors when running on localhost or other domains
	const isOnProductionDomain =
		window.location.hostname.endsWith("podhive.ai") ||
		window.location.hostname === "podhive.ai";

	const options = {
		api_host: apiHost,
		defaults: "2025-11-30" as const,
		// Performance optimizations to prevent blocking
		autocapture: false, // Disable automatic event capture to reduce overhead
		capture_pageview: false, // Don't capture pageview immediately
		loaded: (posthog: any) => {
			// Flush any events that were buffered before PostHog was ready
			AnalyticsService.flushQueue();

			// Defer pageview capture until after PostHog is fully loaded
			if (window.requestIdleCallback) {
				window.requestIdleCallback(() => {
					posthog.capture("$pageview");
				});
			} else {
				setTimeout(() => {
					posthog.capture("$pageview");
				}, 500);
			}
		},
		// Reduce batch size and increase flush interval to reduce network overhead
		batch_size: 10,
		request_batching: true,
		// Use compression for better performance
		compression: true,
		// Don't block on decide endpoint
		advanced_disable_decide: false,
		// Cookie configuration to prevent invalid domain errors
		persistence: "localStorage+cookie" as const,
		cross_subdomain_cookie: isProduction && isOnProductionDomain,
		secure_cookie: isProduction,
		// Only set cookie_domain when actually on the production domain
		...(isProduction &&
			isOnProductionDomain && {
				cookie_domain: ".podhive.ai",
			}),
	};

	return (
		<PostHogProvider apiKey={apiKey} options={options}>
			{children}
		</PostHogProvider>
	);
}
